import { useEffect, useState } from "react";
import {
  Building2, Users, Webhook, KeyRound,
  UserCircle2, FileText, Receipt, Contact2, Truck, Package, ArrowRight, FlaskConical, UserPlus,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { MatchStatsPanel } from "@/components/MatchStatsPanel";
import { MATCH_STATS_ENABLED } from "@/config/features";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { components } from "@dolfin/api-admin";

type ClientStatsResponse = components["schemas"]["ClientStatsResponse"];
type StatKey = keyof Omit<ClientStatsResponse, "clientId" | "asOf">;

const linkedStats = [
  { to: "/organisations", icon: Building2, label: "Organisations", key: "organisations" },
  { to: "/users",         icon: Users,     label: "Portal Members",  key: "portalMembers"  },
  { to: "/webhooks",      icon: Webhook,   label: "Webhooks",      key: "webhookSubscriptions" },
  { to: "/api-keys",      icon: KeyRound,  label: "API keys",      key: "apiKeys" },
] satisfies ReadonlyArray<{ to: string; icon: typeof Building2; label: string; key: StatKey }>;

const unlinkedStats = [
  { icon: UserCircle2, label: "End users", key: "users" },
  { icon: FileText,    label: "Invoices",  key: "invoices" },
  { icon: Contact2,    label: "Customers", key: "customers" },
  { icon: Package,     label: "Products",  key: "products" },
  { icon: Receipt,     label: "Bills",     key: "bills" },
  { icon: Truck,       label: "Suppliers", key: "suppliers" },
] satisfies ReadonlyArray<{ icon: typeof Building2; label: string; key: StatKey }>;

const setupSteps = [
  { to: "/api-keys",      icon: KeyRound,     label: "Mint your first API key" },
  { to: "/webhooks",      icon: Webhook,      label: "Subscribe to bill and invoice events" },
  { to: "/organisations", icon: FlaskConical, label: "Provision a test organisation" },
  { to: "/users",         icon: UserPlus,     label: "Invite teammates" },
];

const numberFmt = new Intl.NumberFormat("en-GB");

function toNumber(v: number | string | undefined | null): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

export function OverviewPage() {
  const { user, activeClientId } = useAuth();
  const [stats, setStats] = useState<ClientStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(false);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(false);
    setStats(null);
    api
      .GET("/v1/client/stats")
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) { setStatsError(true); return; }
        setStats(data);
      })
      .catch(() => { if (!cancelled) setStatsError(true); })
      .finally(() => { if (!cancelled) setStatsLoading(false); });
    return () => { cancelled = true; };
  }, [activeClientId]);

  const renderValue = (key: StatKey) => {
    const value = toNumber(stats?.[key]);
    if (value !== null) return <span className="tabular-nums">{numberFmt.format(value)}</span>;
    return <span className="tabular-nums text-muted-foreground/40">{statsLoading ? "…" : "—"}</span>;
  };

  const asOfLabel = statsError
    ? "Couldn't load stats"
    : stats?.asOf
      ? `Stats as of ${new Date(stats.asOf).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
      : null;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-8">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your organisations, webhooks, API keys and team from here.
          </p>
        </div>
        {/* One freshness stamp for every figure on the page, rather than one per card. */}
        {asOfLabel && (
          <p className={statsError ? "text-xs text-destructive" : "text-xs text-muted-foreground tabular-nums"}>{asOfLabel}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {linkedStats.map((s) => (
          <Link key={s.to} to={s.to}>
            <Card className="h-full bg-card/80 backdrop-blur-sm transition duration-200 hover:border-foreground/20 hover:shadow-lg hover:shadow-foreground/5 motion-safe:hover:-translate-y-0.5">
              <CardContent className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <s.icon className="size-4" />
                  <span className="text-sm">{s.label}</span>
                </div>
                <div className="text-3xl font-bold">{renderValue(s.key)}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Across your organisations
        </h2>
        <Card>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3 lg:grid-cols-6">
              {unlinkedStats.map((s) => (
                <div key={s.key} className="flex flex-col gap-1">
                  <dt className="flex items-center gap-2 text-xs text-muted-foreground">
                    <s.icon className="size-3.5" /> {s.label}
                  </dt>
                  <dd className="text-2xl font-semibold">{renderValue(s.key)}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Get started</CardTitle>
            <CardDescription>One-time setup tasks for new integrations.</CardDescription>
          </CardHeader>
          <CardContent className="-mx-2 flex flex-col text-sm">
            {setupSteps.map((step) => (
              <Link
                key={step.to}
                to={step.to}
                className="group flex items-center gap-3 rounded-md px-2 py-2 transition hover:bg-accent"
              >
                <step.icon className="size-4 text-muted-foreground group-hover:text-secondary" />
                <span className="flex-1">{step.label}</span>
                <ArrowRight className="size-4 text-muted-foreground opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            ))}
          </CardContent>
        </Card>

        <Card className="border-dashed bg-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Activity
              <span className="rounded-full border border-border bg-muted px-1.5 py-0.5 text-[9px] font-normal uppercase tracking-wider text-muted-foreground">
                soon
              </span>
            </CardTitle>
            <CardDescription>Logs are coming soon — webhook deliveries, API calls and audit events in one feed.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Track every event hitting your endpoints, and replay any failed delivery from the portal.
          </CardContent>
        </Card>
      </div>

      {MATCH_STATS_ENABLED && (
        <Card>
          <CardHeader>
            <CardTitle>Three-way matching</CardTitle>
            <CardDescription>
              How much of your bill matching runs without a reviewer, across every organisation you own.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MatchStatsPanel />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
